import { useEffect, useState, useRef } from "react";
import { Loader2, Save, Plus, X, ArrowLeft, RotateCcw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { adminGetPages, adminGetPage, adminUpdatePage, adminResetPage, adminGetSettings, adminUpdateSettings } from "../admin/adminApi";
import { Text, Area, ImageField } from "../admin/ui";
import { useSettings } from "@/context/SettingsContext";

const PAGE_META = {
  "about-india": { label: "About India", route: "/about-india", supportsSections: true, supportsFacts: true },
  "contact": { label: "Contact", route: "/contact", supportsForm: true },
  "faq": { label: "FAQ", route: "/faq" },
  "privacy-policy": { label: "Privacy Policy", route: "/privacy-policy", legal: true },
  "terms": { label: "Terms & Conditions", route: "/terms", legal: true },
  "cancellation-policy": { label: "Cancellation Policy", route: "/cancellation-policy", legal: true },
  "cookie-policy": { label: "Cookie Policy", route: "/cookie-policy", legal: true },
};

export default function CmsPages() {
  const { refresh: refreshSettings } = useSettings();
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(null);
  const [settings, setSettings] = useState(null);
  const originalRef = useRef(null);
  const originalSettingsRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = () => adminGetPages().then(setPages).catch(() => {});
  useEffect(() => { load(); }, []);

  const pageDirty = page && originalRef.current && JSON.stringify(page) !== JSON.stringify(originalRef.current);
  const settingsDirty = settings && originalSettingsRef.current && JSON.stringify(settings) !== JSON.stringify(originalSettingsRef.current);
  const dirty = pageDirty || settingsDirty;

  // Warn on tab close / navigation while dirty
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const openPage = async (slug) => {
    setSelected(slug); setLoading(true);
    try {
      const [p, s] = await Promise.all([
        adminGetPage(slug),
        slug === "contact" ? adminGetSettings() : Promise.resolve(null),
      ]);
      setPage(p);
      originalRef.current = JSON.parse(JSON.stringify(p));
      setSettings(s);
      originalSettingsRef.current = s ? JSON.parse(JSON.stringify(s)) : null;
    } finally { setLoading(false); }
  };

  const goBack = () => {
    if (dirty && !window.confirm("You have unsaved changes. Discard them?")) return;
    setSelected(null); setPage(null); setSettings(null);
    originalRef.current = null; originalSettingsRef.current = null;
  };

  const save = async () => {
    setSaving(true);
    try {
      const tasks = [];
      if (pageDirty) tasks.push(adminUpdatePage(selected, page).then((u) => { setPage(u); originalRef.current = JSON.parse(JSON.stringify(u)); }));
      if (settingsDirty) tasks.push(adminUpdateSettings(settings).then((u) => { setSettings(u); originalSettingsRef.current = JSON.parse(JSON.stringify(u)); refreshSettings(); }));
      await Promise.all(tasks);
      toast.success("Saved. Changes are live.");
      await load();
    } catch (err) {
      toast.error("Could not save. Please try again.");
    } finally { setSaving(false); }
  };

  const restore = async () => {
    if (!window.confirm(`Restore "${PAGE_META[selected]?.label || selected}" to its original default content? Your current edits will be lost.`)) return;
    setResetting(true);
    try {
      const fresh = await adminResetPage(selected);
      setPage(fresh);
      originalRef.current = JSON.parse(JSON.stringify(fresh));
      toast.success("Page restored to defaults.");
      await load();
    } catch (err) {
      toast.error("Restore failed.");
    } finally { setResetting(false); }
  };

  const setField = (k, v) => setPage((p) => ({ ...p, [k]: v }));
  const setSetting = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  if (selected && page) {
    const meta = PAGE_META[selected] || {};
    return (
      <div data-testid="cms-page-editor">
        <button onClick={goBack} className="inline-flex items-center gap-2 text-sm text-[#7c1d1d] mb-6" data-testid="cms-page-back">
          <ArrowLeft className="w-4 h-4" /> Back to all pages
        </button>

        <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#14523d] font-semibold">Editing</div>
            <div className="flex items-center gap-3 mt-1">
              <h1 className="font-serif text-4xl text-[#3b1a1a]">{meta.label || page.title}</h1>
              {dirty && (
                <span data-testid="cms-page-dirty" className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase bg-[#d4af37] text-[#2d1616] px-2.5 py-1 rounded-full font-semibold">
                  <AlertCircle className="w-3 h-3" /> Unsaved
                </span>
              )}
            </div>
            {meta.route && (
              <a href={meta.route} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7c1d1d] underline mt-1 inline-block">
                Preview {meta.route} ↗
              </a>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={restore} disabled={resetting || saving} data-testid="cms-page-restore"
              className="inline-flex items-center gap-2 border border-[#7c1d1d]/40 text-[#7c1d1d] hover:bg-[#7c1d1d] hover:text-white disabled:opacity-60 px-4 py-3 text-xs tracking-[0.24em] uppercase rounded-md transition-colors">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Restore Defaults
            </button>
            <button onClick={save} disabled={saving || !dirty} data-testid="cms-page-save"
              className="inline-flex items-center gap-2 bg-[#14523d] hover:bg-[#0f3d2c] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 text-xs tracking-[0.24em] uppercase rounded-md">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
            </button>
          </div>
        </div>

        <div className="space-y-8">
          {/* Hero */}
          <Card title="Hero Banner">
            <Text label="Hero title" value={page.hero_title} onChange={(v) => setField("hero_title", v)} />
            <Text label="Hero subtitle" value={page.hero_subtitle} onChange={(v) => setField("hero_subtitle", v)} />
            <ImageField label="Hero image (URL or upload)" value={page.hero_image} onChange={(v) => setField("hero_image", v)} folder="pages" />
          </Card>

          {/* Intro */}
          <Card title="Page Introduction">
            <Area label="Intro paragraph" rows={4} value={page.intro} onChange={(v) => setField("intro", v)} placeholder="Shown at the top of the page." />
          </Card>

          {/* Sections editor for About India + Legal pages */}
          {(meta.supportsSections || meta.legal) && (
            <SectionsEditor
              title={meta.supportsSections ? "Editorial Sections" : "Policy Sections"}
              items={page.sections || []}
              onChange={(v) => setField("sections", v)}
              withImage={meta.supportsSections}
            />
          )}

          {/* About India facts */}
          {meta.supportsFacts && (
            <FactsEditor items={page.facts || []} onChange={(v) => setField("facts", v)} />
          )}

          {/* About India CTA */}
          {meta.supportsSections && (
            <Card title="Call to action">
              <Text label="CTA button text" value={page.cta_button} onChange={(v) => setField("cta_button", v)} />
            </Card>
          )}

          {/* Contact-specific */}
          {meta.supportsForm && (
            <Card title="Contact-form copy">
              <Text label="Form title" value={page.form_title} onChange={(v) => setField("form_title", v)} />
              <Text label="Success title" value={page.success_title} onChange={(v) => setField("success_title", v)} />
              <Area label="Success body" rows={2} value={page.success_body} onChange={(v) => setField("success_body", v)} />
            </Card>
          )}

          {/* Contact — inline settings so the user can edit phone/email/address/director/emergency in one place */}
          {meta.supportsForm && settings && (
            <>
              <Card title="Contact Details">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Text label="Primary Phone" value={settings.phone} onChange={(v) => setSetting("phone", v)} placeholder="+91 80773 85030" />
                  <Text label="Alternate Phone" value={settings.phone_alt} onChange={(v) => setSetting("phone_alt", v)} placeholder="+91 99976 11162" />
                  <Text label="WhatsApp Number" value={settings.whatsapp} onChange={(v) => setSetting("whatsapp", v)} placeholder="+91 99976 11162" />
                  <Text label="Email" value={settings.email} onChange={(v) => setSetting("email", v)} placeholder="haseeb@legacyindiatravel.com" />
                  <Text label="Office Hours" value={settings.office_hours} onChange={(v) => setSetting("office_hours", v)} placeholder="Mon – Sat · 9:30 am – 7:00 pm IST" />
                  <Text label="Company Name" value={settings.company_name} onChange={(v) => setSetting("company_name", v)} />
                </div>
                <Text label="Google Map Embed URL" value={settings.map_embed} onChange={(v) => setSetting("map_embed", v)} placeholder="https://www.google.com/maps?q=…&output=embed" />
                <p className="text-[11px] text-[#8a7c66]">These fields appear on the /contact page and in the site footer. Changes save site-wide.</p>
              </Card>

              <OfficesEditor items={settings.offices || []} onChange={(v) => setSetting("offices", v)} />

              <Card title="Director / Contact Person">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Text label="Director Name" value={settings.director_name} onChange={(v) => setSetting("director_name", v)} placeholder="Mr. Haseeb Khan" />
                  <Text label="Director Title" value={settings.director_title} onChange={(v) => setSetting("director_title", v)} placeholder="Director" />
                </div>
                <p className="text-[11px] text-[#8a7c66]">Shown at the top of the contact-info column on /contact.</p>
              </Card>

              <Card title="24/7 Emergency Contact">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Text label="Emergency Contact Name" value={settings.emergency_contact_name} onChange={(v) => setSetting("emergency_contact_name", v)} placeholder="Mr. Gaurav" />
                  <Text label="Emergency Contact Phone" value={settings.emergency_contact_phone} onChange={(v) => setSetting("emergency_contact_phone", v)} placeholder="+91 7017798524" />
                </div>
                <Text label="Emergency Line (secondary text, optional)" value={settings.emergency_line} onChange={(v) => setSetting("emergency_line", v)} placeholder="24/7 WhatsApp & emergency line: +91 99976 11162" />
              </Card>

              <Card title="Floating WhatsApp Button">
                <Area label="Default WhatsApp message" rows={2} value={settings.whatsapp_message} onChange={(v) => setSetting("whatsapp_message", v)} placeholder="Hello, I would like to know more about India tour packages." />
              </Card>
            </>
          )}

          {/* SEO */}
          <Card title="SEO">
            <Text label="Meta title" value={page.seo_title} onChange={(v) => setField("seo_title", v)} />
            <Area label="Meta description" rows={2} value={page.seo_description} onChange={(v) => setField("seo_description", v)} />
          </Card>
        </div>

        {/* Sticky save bar when dirty */}
        {dirty && (
          <div className="fixed bottom-6 right-6 z-40 bg-white border border-[#d4af37] shadow-xl px-5 py-3 rounded-full flex items-center gap-3">
            <span className="text-xs tracking-[0.18em] uppercase text-[#7c1d1d] font-semibold">Unsaved changes</span>
            <button onClick={save} disabled={saving} data-testid="cms-page-save-sticky"
              className="inline-flex items-center gap-2 bg-[#14523d] hover:bg-[#0f3d2c] disabled:opacity-60 text-white px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-full">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save now
            </button>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-[400px] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#7c1d1d]" /></div>;
  }

  return (
    <div data-testid="cms-pages-list">
      <div className="mb-8">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[#14523d] font-semibold">Content</div>
        <h1 className="font-serif text-4xl text-[#3b1a1a] mt-1">Site Pages</h1>
        <p className="text-[#5a4b4b] mt-2">Edit every public page — banners, intros, sections, policies and SEO.</p>
      </div>

      {/* Shortcut cards for pages that live outside the /pages collection */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <a href="/cms/homepage" data-testid="cms-shortcut-homepage" className="text-left bg-[#14523d] hover:bg-[#0f3d2c] text-white p-5 rounded-lg transition-colors">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#e8c970]">Homepage</div>
          <div className="font-serif text-xl mt-1">Edit hero, slides, sections & CTA →</div>
        </a>
        <a href="/cms/tours" className="text-left bg-white hover:border-[#d4af37] border border-[#e5dfd2] p-5 rounded-lg transition-colors">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#7c1d1d]">Tours</div>
          <div className="font-serif text-xl mt-1 text-[#3b1a1a]">Add, edit & publish tour packages →</div>
        </a>
        <a href="/cms/destinations" className="text-left bg-white hover:border-[#d4af37] border border-[#e5dfd2] p-5 rounded-lg transition-colors">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#7c1d1d]">Destinations</div>
          <div className="font-serif text-xl mt-1 text-[#3b1a1a]">Manage every destination page →</div>
        </a>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {pages.map((p) => {
          const meta = PAGE_META[p.slug] || {};
          return (
            <button
              key={p.slug}
              onClick={() => openPage(p.slug)}
              data-testid={`cms-page-${p.slug}`}
              className="text-left bg-white border border-[#e5dfd2] hover:border-[#d4af37] p-5 rounded-lg transition-colors group"
            >
              <div className="flex items-center gap-3">
                {p.hero_image && (
                  <div className="w-20 h-16 rounded overflow-hidden shrink-0 bg-[#faf7ee] border border-[#e5dfd2]">
                    <img src={p.hero_image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-serif text-xl text-[#3b1a1a]">{meta.label || p.title}</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-[#8a7c66] mt-1">{p.slug}</div>
                  <div className="text-xs text-[#14523d] mt-1 truncate">{meta.route}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-[#e5dfd2] rounded-lg p-6 space-y-4">
      <h2 className="font-serif text-xl text-[#3b1a1a]">{title}</h2>
      {children}
    </div>
  );
}

function SectionsEditor({ title, items, onChange, withImage }) {
  const add = () => onChange([...items, { title: "", body: "", ...(withImage ? { image: "", key: `sec-${Date.now()}` } : {}) }]);
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  const move = (i, d) => {
    const arr = [...items];
    const j = i + d;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  return (
    <Card title={title}>
      <div className="space-y-4">
        {items.map((s, i) => (
          <div key={i} className="border border-[#e5dfd2] bg-[#faf7ee] p-4 rounded-md space-y-3" data-testid={`cms-section-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#8a7c66]">Section {i + 1}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs text-[#7c1d1d] disabled:opacity-30">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-xs text-[#7c1d1d] disabled:opacity-30">↓</button>
                <button type="button" onClick={() => del(i)} className="text-[#7c1d1d]" data-testid={`cms-section-del-${i}`}><X className="w-4 h-4" /></button>
              </div>
            </div>
            <Text label="Title" value={s.title} onChange={(v) => upd(i, "title", v)} />
            <Area label="Body" rows={4} value={s.body} onChange={(v) => upd(i, "body", v)} />
            {withImage && <ImageField label="Section image" value={s.image} onChange={(v) => upd(i, "image", v)} folder="pages" />}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        data-testid="cms-section-add"
        className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#14523d] border border-[#14523d]/40 px-4 py-2 rounded-md hover:bg-[#14523d] hover:text-white transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add Section
      </button>
    </Card>
  );
}

function FactsEditor({ items, onChange }) {
  const add = () => onChange([...items, { label: "", value: "" }]);
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  return (
    <Card title="Quick Facts">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((f, i) => (
          <div key={i} className="border border-[#e5dfd2] bg-[#faf7ee] p-3 rounded-md space-y-2">
            <div className="flex justify-end"><button type="button" onClick={() => del(i)}><X className="w-4 h-4 text-[#7c1d1d]" /></button></div>
            <Text label="Label" value={f.label} onChange={(v) => upd(i, "label", v)} />
            <Text label="Value" value={f.value} onChange={(v) => upd(i, "value", v)} />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        data-testid="cms-fact-add"
        className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#14523d] border border-[#14523d]/40 px-4 py-2 rounded-md hover:bg-[#14523d] hover:text-white transition-colors mt-2"
      >
        <Plus className="w-3.5 h-3.5" /> Add Fact
      </button>
    </Card>
  );
}


function OfficesEditor({ items, onChange }) {
  const add = () => onChange([...items, { label: "", name: "Legacy India Travel", address: "", phone: "", email: "" }]);
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  const del = (i) => onChange(items.filter((_, j) => j !== i));
  const move = (i, d) => {
    const arr = [...items];
    const j = i + d;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  return (
    <div className="bg-white border border-[#e5dfd2] rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl text-[#3b1a1a]">Our Offices</h2>
        <span className="text-[11px] text-[#8a7c66]">{items.length} office{items.length === 1 ? "" : "s"}</span>
      </div>
      <div className="space-y-4">
        {items.map((o, i) => (
          <div key={i} className="border border-[#e5dfd2] bg-[#faf7ee] p-4 rounded-md space-y-3" data-testid={`cms-office-${i}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#8a7c66]">Office {i + 1}</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs text-[#7c1d1d] disabled:opacity-30">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-xs text-[#7c1d1d] disabled:opacity-30">↓</button>
                <button type="button" onClick={() => del(i)} className="text-[#7c1d1d]" data-testid={`cms-office-del-${i}`}><X className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Text label="Label (e.g. Head Office)" value={o.label} onChange={(v) => upd(i, "label", v)} placeholder="Head Office" />
              <Text label="Business Name" value={o.name} onChange={(v) => upd(i, "name", v)} placeholder="Legacy India Travel" />
            </div>
            <Area label="Full Address" rows={2} value={o.address} onChange={(v) => upd(i, "address", v)} placeholder="Street, City, State - PIN, Country" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Text label="Phone (optional)" value={o.phone} onChange={(v) => upd(i, "phone", v)} placeholder="+91 …" />
              <Text label="Email (optional)" value={o.email} onChange={(v) => upd(i, "email", v)} placeholder="office@…" />
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        data-testid="cms-office-add"
        className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-[#14523d] border border-[#14523d]/40 px-4 py-2 rounded-md hover:bg-[#14523d] hover:text-white transition-colors"
      >
        <Plus className="w-3.5 h-3.5" /> Add Office
      </button>
      <p className="text-[11px] text-[#8a7c66]">These offices are shown in a 3-card grid on the public /contact page.</p>
    </div>
  );
}
